import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function FavoritesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/favorites"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/favorites/${id}`); setRows((rs) => rs.filter((r) => r.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid={TID.favorites.root}>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="chip"><Heart size={14} /> Your people</span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight">Favorites</h1>
            <p className="mt-2 text-muted-foreground">Save the readers and authors you&apos;d love to work with again.</p>
          </div>
          <Link to="/jobs/new" className="btn-primary">Post project & invite favorites</Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="card-soft h-32 animate-pulse" />)}
          {!loading && rows.length === 0 && (
            <div className="card-soft text-center py-16 col-span-full" data-testid={TID.favorites.empty}>
              <Heart size={26} className="mx-auto text-muted-foreground" />
              <h3 className="mt-4 font-serif text-2xl">No favorites yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Head to Search and heart the people you&apos;d love to work with.</p>
              <Link to="/search" className="btn-primary mt-6 inline-flex">Discover people</Link>
            </div>
          )}
          {rows.map((u) => (
            <article key={u.id} className="card-soft" data-testid={TID.favorites.card}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-serif font-semibold overflow-hidden shrink-0">
                  {u.profile?.avatar_url ? (
                    <img src={u.profile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (u.profile?.display_name || "?")[0]?.toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{u.profile?.display_name || "Anonymous"}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.profile?.headline}</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{u.profile?.bio}</p>
              <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
                <span>Favorited {new Date(u.favorited_at).toLocaleDateString()}</span>
                <button onClick={() => remove(u.id)} data-testid={TID.favorites.remove} className="text-destructive hover:underline">Remove</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
